package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.core.config.AppConfig
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.PageResult
import com.arenaproplay.admin.domain.admin.UserRow
import com.arenaproplay.admin.ui.components.admin.*
// UserTimelineDrawer moved to ui/components/admin/ (R9 sweep)
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.localization.LocalAppStrings
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.countryFlag
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.jetbrains.compose.web.css.*

/** Convert an ISO-8601 string to relative time ("2 мин назад", etc.). */
private fun formatIsoRelative(iso: String?): String {
    if (iso.isNullOrBlank() || iso == "—") return iso ?: "—"
    val millis = Date.parse(iso)
    if (millis.isNaN()) return iso
    val epochMs = millis.toLong()
    val diffMs = Date.now().toLong() - epochMs
    val diffSec = diffMs / 1000
    return when {
        diffSec < 60       -> "только что"
        diffSec < 3600     -> "${diffSec / 60} мин назад"
        diffSec < 86400    -> "${diffSec / 3600} ч назад"
        diffSec < 2592000  -> "${diffSec / 86400} д назад"
        else               -> formatTimestampShort(iso)
    }
}

private fun userInitials(name: String): String {
    val words = name.trim().split(" ").filter { it.isNotEmpty() }
    return when {
        words.size >= 2 -> "${words[0].first().uppercaseChar()}${words[1].first().uppercaseChar()}"
        words.size == 1 && words[0].length >= 2 ->
            "${words[0][0].uppercaseChar()}${words[0][1].uppercaseChar()}"
        words.size == 1 -> words[0].first().uppercaseChar().toString()
        else -> "?"
    }
}

// ── Status → Pill tone mapping ────────────────────────────────────────────────

private fun pillToneForUser(user: UserRow): Pair<String, PillTone> {
    val s = user.status.uppercase()
    return when {
        user.proActive -> Pair("★ PRO", PillTone.Pro)
        s == "ACTIVE" -> Pair("Активен", PillTone.Success)
        s == "BANNED" || s == "BLOCKED" -> Pair("Заблокирован", PillTone.Danger)
        s == "DELETED" -> Pair("Удалён", PillTone.Danger)
        else -> Pair("Без PRO", PillTone.Neutral)
    }
}

// ─────────────────────────────────────────────────────────────────────────────

@Page("/users")
@Composable
fun UsersPage() {
    val strings = LocalAppStrings.current
    val repo = LocalAppContainer.current.adminRepository ?: return

    val scope = rememberCoroutineScope()
    var page by remember { mutableStateOf(urlParam("page")?.toIntOrNull()?.coerceAtLeast(0) ?: 0) }
    var query by remember { mutableStateOf(urlParam("q") ?: "") }
    var pageSize by remember {
        mutableStateOf(urlParam("size")?.toIntOrNull()?.takeIf { it in PAGE_SIZE_OPTIONS } ?: 25)
    }
    var data by remember { mutableStateOf<PageResult<UserRow>?>(null) }
    var reloadToken by remember { mutableStateOf(0) }
    var exporting by remember { mutableStateOf(false) }
    val loadState = rememberLoadState()

    // ── Drawer state: selected user for the right-side detail drawer ──────────
    var selectedUser by remember { mutableStateOf<UserRow?>(null) }
    // ── Timeline (Журнал действий) — shown as an overlapping drawer ──────────
    // selectedUser is kept while the timeline is open so that closing the
    // journal restores the user drawer (drawer is hidden via open flag below).
    var timelineUserId by remember { mutableStateOf<String?>(null) }

    // ── PRO grant / revoke (confirmation) ────────────────────────────────────
    var pendingPro by remember { mutableStateOf<UserRow?>(null) }
    var proWorking by remember { mutableStateOf(false) }
    var proError by remember { mutableStateOf<String?>(null) }

    // ── Delete confirmation ────────────────────────────────────────────────────
    var pendingDelete by remember { mutableStateOf<UserRow?>(null) }
    var deleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }

    // ── Avatar moderation ──────────────────────────────────────────────────────
    var pendingAvatarMod by remember { mutableStateOf<UserRow?>(null) }

    LaunchedEffect(page, query, pageSize) {
        setUrlParams(
            "page" to page.takeIf { it > 0 }?.toString(),
            "q" to query,
            "size" to pageSize.takeIf { it != 25 }?.toString(),
        )
    }

    LaunchedEffect(page, query, pageSize, reloadToken, loadState.reloadToken) {
        data = null
        loadState.load {
            data = repo.getUsers(page, pageSize, query)
        }
    }

    AdminLayout(
        title = strings.navUsers,
        subtitle = "Управление пользователями платформы",
        actions = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                ExportCsvButton(exporting = exporting) {
                    exporting = true
                    scope.launch {
                        try {
                            val all = fetchAllPages { p, size -> repo.getUsers(p, size, query) }
                            downloadCsv(
                                filename = "users.csv",
                                header = listOf(
                                    "ID", "Имя", "Email", "Роль", "Платформы",
                                    "Зарегистрирован", "Активен", "Статус", "PRO",
                                ),
                                rows = all.map {
                                    listOf(
                                        it.id, it.name, it.email, it.role,
                                        it.platforms.joinToString("; "),
                                        it.createdAt, it.lastActive, it.status,
                                        if (it.proActive) "PRO (${it.proSource ?: "?"})" else "—",
                                    )
                                },
                            )
                        } finally {
                            exporting = false
                        }
                    }
                }
                HSpace(8)
                SearchInput(
                    value = query,
                    onValueChange = { query = it; page = 0 },
                    placeholderText = "Поиск по имени или email…",
                )
            }
        }
    ) {
        // ── Error state ────────────────────────────────────────────────────────
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        // ── Result count sub-header ────────────────────────────────────────────
        val total = data?.total ?: 0
        if (data != null) {
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (query.isNotBlank())
                        "Найдено: $total пользователей"
                    else
                        "Всего: $total пользователей",
                    Modifier.bodySMedium().color(AppColors.Muted)
                )
            }
            VSpace(10)
        }

        // ── DataTable v2 inside SectionCard ───────────────────────────────────
        SectionCard(
            modifier = Modifier.fillMaxWidth()
        ) {
            DataTable(
                items = data?.items.orEmpty(),
                loading = data == null,
                onRowClick = { selectedUser = it },
                // Default sort: АКТИВНОСТЬ desc (column index 5)
                defaultSortIndex = 5,
                defaultSortAsc = false,
                columns = listOf(
                    // Column 1: avatar initials + name + email
                    TableColumn<UserRow>(
                        header = "ПОЛЬЗОВАТЕЛЬ",
                        weight = 2.2f,
                        sortKey = { it.name }
                    ) { row ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            AvatarBubble(seed = row.id, initials = userInitials(row.name), sizePx = 34)
                            HSpace(10)
                            Column {
                                SpanText(row.name, Modifier.bodyMMedium().color(AppColors.Ink))
                                Box(Modifier.height(2.px))
                                SpanText(
                                    row.email,
                                    Modifier.bodySMedium().color(AppColors.Muted)
                                        .styleModifier { property("font-size", "11px") }
                                )
                            }
                        }
                    },

                    // Column 2: status Pill (PRO = Pro, Active = Success, banned/deleted = Danger, else Neutral)
                    TableColumn<UserRow>(
                        header = "СТАТУС",
                        weight = 1.1f,
                        sortKey = { if (it.proActive) "0" else it.status }
                    ) { row ->
                        val (label, tone) = pillToneForUser(row)
                        Pill(text = label, tone = tone)
                    },

                    // Column 3: platforms
                    TableColumn<UserRow>(
                        header = "ПЛАТФОРМЫ",
                        weight = 1.3f,
                    ) { row ->
                        if (row.platforms.isEmpty()) {
                            SpanText("—", Modifier.bodyMRegular().color(AppColors.Faint))
                        } else {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                row.platforms.forEachIndexed { i, p ->
                                    if (i > 0) HSpace(5)
                                    UserPlatformPill(p)
                                }
                            }
                        }
                    },

                    // Column 4: geo (flag + city ?? country)
                    TableColumn<UserRow>(
                        header = "ГЕО",
                        weight = 1.0f,
                        sortKey = { it.city ?: it.country }
                    ) { row ->
                        val geoText = row.city ?: row.country
                        if (geoText != null) {
                            val flag = if (row.country != null) "${countryFlag(row.country)} " else ""
                            SpanText(
                                "$flag$geoText",
                                Modifier.bodyMRegular().color(AppColors.Muted)
                                    .styleModifier { property("font-size", "11px") }
                            )
                        } else {
                            SpanText("—", Modifier.bodyMRegular().color(AppColors.Faint))
                        }
                    },

                    // Column 5: registered date (default sort = newest first)
                    TableColumn<UserRow>(
                        header = "РЕГИСТРАЦИЯ",
                        weight = 1.1f,
                        sortKey = { it.createdAt }
                    ) { row ->
                        SpanText(
                            formatTimestampShort(row.createdAt),
                            Modifier.bodyMRegular().color(AppColors.Muted)
                                .styleModifier { property("font-size", "11px") }
                        )
                    },

                    // Column 6: last active (relative time, default sort = most recent first).
                    // sortKey: "—" (no activity) → null so it sinks to the bottom instead of
                    // string-sorting above ISO dates.
                    TableColumn<UserRow>(
                        header = "АКТИВНОСТЬ",
                        weight = 1.1f,
                        sortKey = { it.lastActive.takeIf { ts -> !Date.parse(ts).isNaN() } }
                    ) { row ->
                        val ms = Date.parse(row.lastActive)
                        val isOnline = !ms.isNaN() && Date.now() - ms < 5 * 60_000
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            if (isOnline) {
                                Box(
                                    Modifier.width(6.px).height(6.px).borderRadius(50.percent)
                                        .styleModifier {
                                            property("background", "#00E5A4")
                                            property("box-shadow", "0 0 8px #00E5A4")
                                            property("flex-shrink", "0")
                                        }
                                )
                                HSpace(6)
                            }
                            SpanText(
                                if (isOnline) "онлайн" else formatIsoRelative(row.lastActive),
                                Modifier.bodyMRegular()
                                    .styleModifier {
                                        property("font-size", "11px")
                                        property("color", if (isOnline) "#00E5A4" else "rgba(240,244,252,.45)")
                                    }
                            )
                        }
                    },
                ),
                emptyText = strings.emptyData,
            )
        }

        VSpace(12)

        PaginationBar(
            page = page,
            pageSize = pageSize,
            total = data?.total ?: 0,
            onPrev = { if (page > 0) page-- },
            onNext = {
                val t = data?.total ?: 0
                if ((page + 1) * pageSize < t) page++
            },
            onPageSizeChange = { pageSize = it; page = 0 }
        )

        VSpace(28)
    }

    // ── User detail drawer ────────────────────────────────────────────────────
    val user = selectedUser
    Drawer(
        open = user != null && timelineUserId == null,
        onClose = { selectedUser = null },
        title = if (user != null) user.name else "Пользователь",
    ) {
        if (user != null) {
            UserDetailDrawerContent(
                user = user,
                onOpenProfile = {
                    window.open(AppConfig.getProfileUrl(user.id), "_blank", "noopener,noreferrer")
                },
                onGrantRevokePro = {
                    pendingPro = user
                    proError = null
                },
                onOpenTimeline = {
                    // Keep selectedUser set — drawer hides via open flag,
                    // closing the journal will reveal the drawer again.
                    timelineUserId = user.id
                },
                onDelete = {
                    pendingDelete = user
                    deleteError = null
                    selectedUser = null
                },
                onRemoveAvatar = { pendingAvatarMod = user },
            )
        }
    }

    // ── Timeline (Журнал действий) drawer ─────────────────────────────────────
    UserTimelineDrawer(
        userId = timelineUserId ?: "",
        open = timelineUserId != null,
        onClose = { timelineUserId = null },
    )

    // ── PRO grant / revoke confirmation ───────────────────────────────────────
    val proTarget = pendingPro
    val revoking = proTarget?.proActive == true
    if (proTarget != null) {
        ConfirmActionOverlay(
            isOpen = true,
            onClose = { if (!proWorking) { pendingPro = null; proError = null } },
            title = if (revoking) "Отозвать PRO" else "Выдать PRO",
            description = if (revoking)
                "Это немедленно прекратит PRO-доступ для ${proTarget.name} (${proTarget.email})."
            else
                "Выдаёт ${proTarget.name} (${proTarget.email}) полный PRO-доступ — " +
                    "события PRO, AI аватары, премиум карточки — до ручного отзыва. " +
                    "Не учитывается в выручке и KPI подписок.",
            warning = if (revoking && proTarget.proSource != null && proTarget.proSource != "ADMIN")
                "Внимание: это подписка ${proTarget.proSource} через стор. " +
                    "Она восстановится при следующей проверке покупки в приложении."
            else null,
            error = proError,
            confirmLabel = when {
                proWorking -> if (revoking) "Отзываем…" else "Выдаём…"
                revoking -> "Отозвать PRO"
                else -> "Выдать PRO"
            },
            confirmDanger = revoking,
            confirmEnabled = !proWorking,
        ) {
            proWorking = true
            proError = null
            val target = proTarget
            scope.launch {
                try {
                    if (revoking) repo.revokePro(target.id) else repo.grantPro(target.id)
                    pendingPro = null
                    reloadToken++
                } catch (e: Throwable) {
                    proError = e.message ?: "Ошибка запроса"
                } finally {
                    proWorking = false
                }
            }
        }
    }

    // ── Delete confirmation ────────────────────────────────────────────────────
    val deleteTarget = pendingDelete
    if (deleteTarget != null) {
        ConfirmActionOverlay(
            isOpen = true,
            onClose = { if (!deleting) { pendingDelete = null; deleteError = null } },
            title = "Удалить пользователя",
            description = "Это безвозвратно удалит ${deleteTarget.name} (${deleteTarget.email}). " +
                "Публикации и истории удаляются, аккаунт анонимизируется — " +
                "email освобождается для повторной регистрации.",
            warning = null,
            error = deleteError,
            confirmLabel = if (deleting) "Удаляем…" else "Удалить",
            confirmDanger = true,
            confirmEnabled = !deleting,
        ) {
            deleting = true
            deleteError = null
            val target = deleteTarget
            scope.launch {
                try {
                    repo.deleteUser(target.id)
                    pendingDelete = null
                    reloadToken++
                } catch (e: Throwable) {
                    deleteError = e.message ?: "Ошибка удаления"
                } finally {
                    deleting = false
                }
            }
        }
    }

    // ── Avatar moderation: диалог точечного удаления фото ─────────────────────
    val avatarModTarget = pendingAvatarMod
    if (avatarModTarget != null) {
        AvatarModerationDialog(
            userId = avatarModTarget.id,
            userName = avatarModTarget.name,
            repo = repo,
            onDismiss = { pendingAvatarMod = null },
            // Аватар в drawer (UserRow) не обновляется автоматически,
            // т.к. UserRow не несёт avatarUrl — достаточно закрыть диалог.
        )
    }
}

// ── User detail drawer content ────────────────────────────────────────────────

@Composable
private fun UserDetailDrawerContent(
    user: UserRow,
    onOpenProfile: () -> Unit,
    onGrantRevokePro: () -> Unit,
    onOpenTimeline: () -> Unit,
    onDelete: () -> Unit,
    onRemoveAvatar: () -> Unit,
) {
    Column(Modifier.fillMaxWidth()) {
        // Avatar + name block
        Row(
            Modifier.fillMaxWidth()
                .sBorderRadius(12)
                .sPadding(14)
                .styleModifier {
                    property("background", AppColors.Background.toRgbString())
                    property("border", "1px solid ${AppColors.Border.toRgbString()}")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            AvatarBubble(seed = user.id, initials = userInitials(user.name), sizePx = 48)
            HSpace(12)
            Column(Modifier.weight(1f)) {
                SpanText(
                    user.name,
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .styleModifier { property("font-size", "15px"); property("font-weight", "700") }
                )
                Box(Modifier.height(3.px))
                SpanText(user.email, Modifier.bodySMedium().color(AppColors.Muted))
            }
            // PRO / Free pill
            val (pillLabel, pillTone) = pillToneForUser(user)
            Pill(text = pillLabel, tone = pillTone)
        }

        VSpace(16)

        // Meta info rows
        DrawerMetaRow("ID", "#${user.id.take(12)}", mono = true)
        DrawerMetaRow("Роль", user.role)
        DrawerMetaRow("Статус", user.status)
        DrawerMetaRow("Зарегистрирован", formatTimestampShort(user.createdAt))
        DrawerMetaRow("Последняя активность", formatTimestampShort(user.lastActive))
        if (user.platforms.isNotEmpty()) {
            DrawerMetaRow("Платформы", user.platforms.joinToString(", "))
        }
        if (user.proActive && user.proSource != null) {
            DrawerMetaRow("Источник PRO", user.proSource)
        }
        if (user.proUntil != null) {
            DrawerMetaRow("PRO до", formatTimestampShort(user.proUntil))
        }

        VSpace(20)

        // ── Action buttons ────────────────────────────────────────────────────
        DrawerActionButton(
            label = "Открыть профиль",
            icon = "external-link",
            bgColor = AppColors.Ink.toRgbString(),
            fgColor = AppColors.Surface.toRgbString(),
            onClick = onOpenProfile,
        )

        VSpace(8)

        DrawerActionButton(
            label = if (user.proActive) "Отозвать PRO" else "Выдать PRO",
            icon = "crown",
            bgColor = if (user.proActive) AppColors.SurfaceMuted.toRgbString()
                      else AppColors.Accent.WarningTint.toRgbaString(1f),
            fgColor = if (user.proActive) AppColors.Muted.toRgbString()
                      else AppColors.ProGold.toRgbString(),
            borderColor = if (user.proActive) AppColors.Ink.toRgbaString(0.08f)
                          else AppColors.ProGold.toRgbaString(0.22f),
            onClick = onGrantRevokePro,
        )

        VSpace(8)

        DrawerActionButton(
            label = "Журнал действий",
            icon = "clock",
            bgColor = AppColors.PrimaryTint.toRgbString(),
            fgColor = AppColors.Primary.toRgbString(),
            borderColor = AppColors.Primary.toRgbaString(0.18f),
            onClick = onOpenTimeline,
        )

        VSpace(8)

        DrawerActionButton(
            label = "Удалить фото (модерация)",
            icon = "image-off",
            bgColor = AppColors.ErrorTint.toRgbString(),
            fgColor = AppColors.Error.toRgbString(),
            borderColor = AppColors.Error.toRgbaString(0.18f),
            onClick = onRemoveAvatar,
        )

        VSpace(8)

        DrawerActionButton(
            label = "Удалить пользователя",
            icon = "trash-2",
            bgColor = AppColors.ErrorTint.toRgbString(),
            fgColor = AppColors.Error.toRgbString(),
            borderColor = AppColors.Error.toRgbaString(0.18f),
            onClick = onDelete,
        )
    }
}

@Composable
private fun DrawerMetaRow(label: String, value: String, mono: Boolean = false) {
    Row(
        Modifier.fillMaxWidth()
            .padding(topBottom = 7.px)
            .styleModifier {
                property("border-bottom", "1px solid ${AppColors.SurfaceMuted.toRgbString()}")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            label,
            Modifier.bodySMedium().color(AppColors.Faint)
                .styleModifier {
                    property("width", "140px")
                    property("flex-shrink", "0")
                    property("font-size", "11.5px")
                }
        )
        SpanText(
            value,
            if (mono) Modifier.mono(11).color(AppColors.Muted)
            else Modifier.bodySMedium().color(AppColors.Ink)
                .styleModifier { property("font-size", "11.5px") }
        )
    }
}

@Composable
private fun DrawerActionButton(
    label: String,
    icon: String,
    bgColor: String,
    fgColor: String,
    borderColor: String? = null,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(10)
            .padding(leftRight = 16.px, topBottom = 10.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", bgColor)
                if (borderColor != null) property("border", "1px solid $borderColor")
                property("transition", "opacity 140ms ease")
            }
            .onClick { onClick() },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            LucideIcon(name = icon, size = 15, color = fgColor)
            HSpace(9)
            SpanText(
                label,
                Modifier.bodySMedium().color(Color(fgColor))
                    .styleModifier { property("font-weight", "600") }
            )
        }
    }
}

// ── Platform pill ─────────────────────────────────────────────────────────────

@Composable
private fun UserPlatformPill(p: String) {
    val (bg, fg, label) = when (p.lowercase()) {
        "ios" -> Triple(
            AppColors.Success.toRgbaString(0.14f),
            AppColors.Success.toRgbString(),
            "iOS"
        )
        "android" -> Triple(
            AppColors.ChartSoft.toRgbaString(0.50f),
            AppColors.PrimaryHover.toRgbString(),
            "Android"
        )
        "web" -> Triple(
            AppColors.Primary.toRgbaString(0.12f),
            AppColors.Primary.toRgbString(),
            "Web"
        )
        "admin-web" -> Triple(
            AppColors.Warning.toRgbaString(0.16f),
            AppColors.Warning.toRgbString(),
            "Admin"
        )
        else -> Triple(
            AppColors.Ink.toRgbaString(0.05f),
            AppColors.Muted.toRgbString(),
            p
        )
    }
    Box(
        Modifier
            .padding(leftRight = 8.px, topBottom = 2.px)
            .sBorderRadius(999)
            .styleModifier { property("background", bg) },
    ) {
        SpanText(
            label,
            Modifier.bodySMedium().color(Color(fg))
                .styleModifier { property("font-size", "12px") }
        )
    }
}

// PaginationBar, PagerButton, PAGE_SIZE_OPTIONS — moved to ui/components/admin/Pagination.kt

